/* ============================================================
 * 归途 · Homeward —— 网络层（第一阶段：窗口聊天）
 *
 * 设计目标：
 *   - 与游戏逻辑解耦：网络断开不影响单机玩法
 *   - 自带断线重连（指数退避）、心跳、离线消息队列
 *   - 只依赖浏览器原生 WebSocket，不引入第三方库
 *
 * 用法：
 *   const net = createChatClient({
 *     url: import.meta.env.VITE_WS_URL || 'ws://localhost:3001',
 *     nick: '旅人',
 *     onMessage: msg => { ... },
 *     onStatus: st => { ... }   // 'connecting' | 'online' | 'offline'
 *   });
 *   net.send({ type: 'chat', channel: 'world', text: '有人组队吗' });
 *   net.close();
 * ============================================================ */

const PROTOCOL_VERSION = 1;
const HEARTBEAT_MS = 25000;
const MAX_QUEUE = 50;
const MAX_TEXT = 100;

export function createChatClient(options) {
  const opts = options || {};
  const url = opts.url || 'ws://localhost:3001';
  const clientId = opts.clientId || randomId();
  const nick = opts.nick || '旅人';
  const token = opts.token || '';
  const onMessage = opts.onMessage || function () {};
  const onStatus = opts.onStatus || function () {};

  let ws = null;
  let retry = 0;
  let closedByUser = false;
  let heartbeatTimer = null;
  let reconnectTimer = null;
  const queue = [];

  function randomId() {
    return 'c' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  }

  function status(st) {
    try { onStatus(st); } catch (e) { /* 忽略回调异常 */ }
  }

  function sendNow(payload) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }

  // 未连接时先入队，连上后按顺序补发
  function send(payload) {
    if (!payload || typeof payload !== 'object') return false;
    if (payload.type === 'chat' && typeof payload.text === 'string') {
      payload.text = payload.text.slice(0, MAX_TEXT);
    }
    if (sendNow(payload)) return true;
    if (queue.length < MAX_QUEUE) queue.push(payload);
    return false;
  }

  function startHeartbeat() {
    stopHeartbeat();
    heartbeatTimer = setInterval(function () {
      sendNow({ type: 'ping', ts: Date.now() });
    }, HEARTBEAT_MS);
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  // 连续失败多次后放慢重连（最多 60 秒一次），避免服务端没起来时刷屏
  function scheduleReconnect() {
    if (closedByUser || reconnectTimer) return;
    const delay = Math.min(15000, 1000 * Math.pow(2, retry));
    retry += 1;
    reconnectTimer = setTimeout(function () {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  function connect() {
    closedByUser = false;
    status('connecting');
    try {
      ws = new WebSocket(url);
    } catch (e) {
      scheduleReconnect();
      return;
    }

    ws.onopen = function () {
      retry = 0;
      status('online');
      sendNow({ type: 'hello', version: PROTOCOL_VERSION, clientId: clientId, nick: nick, token: token || undefined });
      while (queue.length) sendNow(queue.shift());
      startHeartbeat();
    };

    ws.onmessage = function (event) {
      let msg = null;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (msg && msg.type === 'ping') { sendNow({ type: 'pong', ts: Date.now() }); return; }
      try { onMessage(msg); } catch (e) { /* 忽略回调异常 */ }
    };

    ws.onclose = function () {
      stopHeartbeat();
      status('offline');
      scheduleReconnect();
    };

    ws.onerror = function () {
      // onclose 会紧随其后触发，这里只标记状态
      status('offline');
    };
  }

  function close() {
    closedByUser = true;
    stopHeartbeat();
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (ws) ws.close();
    ws = null;
  }

  connect();

  return {
    send: send,
    close: close,
    get clientId() { return clientId; },
    get nick() { return nick; },
    get status() { return ws && ws.readyState === WebSocket.OPEN ? 'online' : 'offline'; }
  };
}

export const NET_TEXT_LIMIT = MAX_TEXT;

/* ============================================================
 * 服务器地址处理
 *   登录页里那个「服务器 IP」输入框可以填：
 *     192.168.1.10               → http://192.168.1.10:8787
 *     192.168.1.10:9000          → http://192.168.1.10:9000
 *     http://game.example.com    → 原样（走 nginx 反代时用同源地址即可）
 *     空                          → 当前页面同源（nginx 反代 /api、/ws）
 *     与当前页面同一个域名（如正式站上默认填的 wuzhe.online）
 *                                 → 当前页面同源（走 nginx 反代，不会去连 :8787）
 * ============================================================ */
const DEFAULT_PORT = 8787;

export function normalizeServerUrl(input) {
  let s = String(input == null ? '' : input).trim();
  if (!s) {
    if (typeof location !== 'undefined' && /^https?:$/.test(location.protocol)) return location.origin;
    return 'http://127.0.0.1:' + DEFAULT_PORT;
  }
  if (!/^https?:\/\//i.test(s)) s = 'http://' + s;
  s = s.replace(/\/+$/, '');
  try {
    const u = new URL(s);
    // 和当前页面是同一个域名、而且没写端口 → 当成"同源"，交给 nginx 反代 /api、/ws。
    // 这样正式站上默认填的 wuzhe.online 不会变成 wuzhe.online:8787（那会绕过 nginx）。
    if (typeof location !== 'undefined' && u.hostname === location.hostname && !u.port) {
      return location.origin;
    }
    // 只写了 IP / 域名，没写端口 → 补默认端口
    if (!u.port) u.port = String(DEFAULT_PORT);
    return u.origin;
  } catch (e) {
    return s;
  }
}

export function wsUrlFrom(baseUrl, params) {
  const u = String(baseUrl || '').replace(/^http/i, 'ws');
  const q = new URLSearchParams(params || {}).toString();
  return u + '/ws' + (q ? '?' + q : '');
}

/* ============================================================
 * 账号 / 存档 API 客户端
 *   const api = createApiClient({ baseUrl: 'http://127.0.0.1:8787' });
 *   await api.register('张三', '123456');
 *   const r = await api.login('张三', '123456');   // { token, save, version, savedAt }
 *   await api.pushSave(token, saveObj, savedAt);
 *   const s = await api.fetchSave(token);
 *
 * 所有方法失败时抛 ApiError（带 code / status），网络不通时 code = 'offline'，
 * 调用方据此回退到本地存档即可。
 * ============================================================ */
export class ApiError extends Error {
  constructor(message, code, status) {
    super(message);
    this.name = 'ApiError';
    this.code = code || 'error';
    this.status = status || 0;
  }
}

export function createApiClient(options) {
  const opts = options || {};
  const baseUrl = normalizeServerUrl(opts.baseUrl);
  const timeoutMs = opts.timeoutMs || 8000;

  async function request(method, path, body, token) {
    const headers = {};
    if (body !== undefined && body !== null) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = 'Bearer ' + token;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(function () { ctrl.abort(); }, timeoutMs) : null;
    let res = null;
    try {
      res = await fetch(baseUrl + path, {
        method: method,
        headers: headers,
        body: body === undefined || body === null ? undefined : JSON.stringify(body),
        signal: ctrl ? ctrl.signal : undefined
      });
    } catch (e) {
      if (timer) clearTimeout(timer);
      throw new ApiError('连接服务器失败（' + baseUrl + '）', 'offline', 0);
    }
    if (timer) clearTimeout(timer);
    let data = null;
    try { data = await res.json(); } catch (e) { /* 可能没有 body */ }
    if (!res.ok || !data || data.ok === false) {
      const err = (data && data.error) || {};
      throw new ApiError(err.message || ('请求失败 HTTP ' + res.status), err.code || 'http_' + res.status, res.status);
    }
    return data;
  }

  return {
    baseUrl: baseUrl,
    health: () => request('GET', '/api/health'),
    register: (user, pwd) => request('POST', '/api/register', { user: user, pwd: pwd }),
    login: (user, pwd) => request('POST', '/api/login', { user: user, pwd: pwd }),
    // 带设备标识登录：同设备刷新不会被"单设备登录"挡住，换设备才会提示"账号已登录"
    loginWithDevice: (user, pwd, deviceId) =>
      request('POST', '/api/login', { user: user, pwd: pwd, deviceId: deviceId }),
    logout: (token) => request('POST', '/api/logout', null, token),
    fetchSave: (token) => request('GET', '/api/save', null, token),
    pushSave: (token, save, savedAt) => request('PUT', '/api/save', { save: save, savedAt: savedAt || Date.now() }, token),
    chatHistory: (channel, limit) =>
      request('GET', '/api/chat/history?channel=' + encodeURIComponent(channel || 'world') + '&limit=' + (limit || 50)),
    // 实名信息
    profile: (token) => request('GET', '/api/profile', null, token),
    setNickname: (token, nickname) => request('POST', '/api/profile/nickname', { nickname: nickname }, token),
    bindIdentity: (token, realName, idCard) =>
      request('POST', '/api/profile/identity', { realName: realName, idCard: idCard }, token),
    stats: () => request('GET', '/api/stats')
  };
}
